import { LoadingStatus, SavingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { useConfigFields } from 'hooks/useConfigFields';
import { useEffect, useMemo, useState } from 'react';
import {
    createNewFormField,
    selectConfigFieldsErrorMessage,
    selectConfigFieldsSavingStatus,
    updateConfigFields,
} from 'features/configFields/configFieldsSlice';

import { useStyles } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { FormTable } from 'components/Forms/FormTable';
import { Alert, AlertTitle } from '@mui/material';
import { selectDirtyFormFlag, setIsDirty } from 'features/common/formSlice';
import { LoadingLogo } from 'GeminiViewerComponent/components/LoadingLogo/LoadingLogo';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';

const ConfigFieldsManager = () => {
    //#region Hooks

    const theme = useSelector(selectDefaultTheme);
    const styles = useStyles(theme);

    const dispatch = useDispatch();
    const getDirty = useSelector(selectDirtyFormFlag);
    const { configFieldsLoadingStatus, configFields, validationTypes } = useConfigFields();

    useEffect(() => {
        console.log('form config fields changed');
        setFormConfigFields(configFields);
    }, [configFields]);

    //#endregion Hooks

    //#region State
    const [formConfigFields, setFormConfigFields] = useState(configFields);
    const [isDirty, setDirty] = useState(false);

    useEffect(() => {
        if (getDirty === true) {
            setDirty(true);
        }
    }, [getDirty]);
    //#endregion State

    //#region Selectors
    const configFieldsSavingStatus = useSelector(selectConfigFieldsSavingStatus);
    const errorMessage = useSelector(selectConfigFieldsErrorMessage);
    //#endregion Selectors

    //#region Refs
    //#endregion Refs

    //#region Constants

    const handleSubmit = (values) => {
        dispatch(updateConfigFields({ values: values }));
        dispatch(setIsDirty(false));
        setDirty(false);
    };

    const handleCancel = () => {
        setDirty(false);
        setFormConfigFields(configFields);
        dispatch(setIsDirty(false));
    };

    const formValues = useMemo(() => {
        const values = {};
        formConfigFields.forEach((value) => {
            values[value?.config_field_id] = value;
        });
        return values;
    }, [formConfigFields]);

    const formTableDefinition = {
        createMethod: createNewFormField,
        submitMethod: handleSubmit,
        handleCancel: handleCancel,
        setStateMethod: setFormConfigFields,
        selectFormState: formConfigFields,
        categoryKey: 'category',
        key: 'config_field_id',
        allowDelete: true,
        class: styles.formTable,
        formValues: formValues,
        columns: [
            {
                config: { type: 'text' },
                title: 'Name',
                class: styles.nameColumn,
                key: 'display_name',
                width: '25%',
                validation: {},
            },
            {
                config: {
                    type: 'select',
                    options: validationTypes,
                    options_key: 'validation_type_id',
                    options_value_key: 'validation_type_id',
                    options_display_key: 'display_name',
                },
                title: 'Validation',
                class: styles.typeColumn,
                key: 'validation_type_id',
                validation: {},
            },
            {
                config: { type: 'checkbox' },
                title: 'Is Feature',
                class: styles.featureColumn,
                key: 'is_feature',
                validation: {},
            },
            {
                config: { type: 'text' },
                title: 'Key',
                class: styles.typeColumn,
                key: 'key',
                validation: {},
            },
            {
                config: { type: 'text' },
                title: 'Category',
                class: styles.typeColumn,
                key: 'category',
                // useful for category fields where the field will jump to a new category section
                // if we allow updating during entry
                updateOnSubmit: true,
                validation: {},
            },
        ],
    };

    //#endregion Constants

    //#region Render time calcs

    const isBusy =
        configFieldsLoadingStatus === LoadingStatus.Loading ||
        configFieldsLoadingStatus === LoadingStatus.Idle ||
        configFieldsSavingStatus === SavingStatus.Saving;

    //#endregion Render time calcs

    //#region Effects
    //#endregion Effects

    //#region Methods
    //#endregion Methods

    //#region Render
    //#endregion Render
    // eslint-disable-next-line no-unused-vars

    return isBusy ? (
        <LoadingLogo />
    ) : (
        <>
            {errorMessage && (
                <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    {errorMessage}
                </Alert>
            )}
            <div className={`${styles.configContainer} ${isDirty ? styles.unsaveData : ''}`}>
                <FormTable tableDefinition={formTableDefinition} />
            </div>
        </>
    );
};

export { ConfigFieldsManager };
